// Schmaler Fetch-Wrapper. Alle Antworten der API haben entweder den erwarteten
// Inhalt oder die Fehlerform { code, message }.

export interface ApiErrorBody {
  code: string
  message: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
  }
}

async function parseError(res: Response): Promise<ApiError> {
  let body: ApiErrorBody = { code: 'unknown', message: `Unerwartete Antwort (${res.status})` }
  try {
    const parsed = (await res.json()) as Partial<ApiErrorBody>
    if (parsed && typeof parsed.message === 'string') {
      body = { code: parsed.code ?? 'unknown', message: parsed.message }
    }
  } catch {
    // Antwort war kein JSON – die Standardmeldung bleibt stehen.
  }
  return new ApiError(res.status, body)
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!res.ok) throw await parseError(res)
  return (await res.json()) as T
}

/**
 * Der eigene Kopfzeilen-Nachweis gegen CSRF. Ein fremdes Formular kann ihn
 * nicht setzen, deshalb reicht seine Anwesenheit dem Server als Beleg, dass
 * die Anfrage aus dieser Anwendung kommt.
 */
const CSRF_HEADER = 'X-Netzwarte-CSRF'

export async function apiSend<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      [CSRF_HEADER]: '1',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) throw await parseError(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Meldet, ob die Antwort eine abgelaufene Anmeldung war. */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401
}

export interface Health {
  status: string
  version: string
  commit: string
  database: string
  schema_version: number
  templates: number
  uptime_seconds: number
}
